import type { CubeLut } from './compositing';

/**
 * Colour management: camera log curves and wide gamuts converted into the
 * working space (Rec.709 primaries, gamma 2.4 — what the preview shows and
 * the grade works in), and the project's output space for exports.
 *
 * Both are per-pixel functions, so like the grade they are baked into 3D LUTs
 * that the preview samples as textures and the export runs through `lut3d`.
 * The input transform is the first colour step of a clip (before the basic
 * adjustments and the grade); the output transform is the last step of the
 * export (the preview stays in the working space, like a Rec.709 monitor).
 *
 * Curves follow the manufacturers' published formulas. Video decoders expand
 * legal-range video (code values 64..940) to 0..1, while Sony, Panasonic and
 * ARRI define their curves on full-scale code values (CV / 1023), so those
 * are applied to (v·876 + 64) / 1023. Canon Log 3 is defined on the
 * legal-range signal (IRE), and Blackmagic Film Gen 5 on full-range data,
 * so those take v directly.
 */

export type InputSpaceId = 'rec709' | 'srgb' | 'rec2020' | 'hlg' | 'pq' | 'slog3' | 'vlog' | 'logc3' | 'logc4' | 'clog3' | 'bmdfilm5';
export type ToneMapId = 'none' | 'soft';
export type OutputSpaceId = 'rec709' | 'rec2020' | 'hlg' | 'pq';

export interface ColorSpaceInfo<T extends string> {
  id: T;
  label: string;
  /** Scene-referred or HDR: highlights go far above the working white (tone mapping helps). */
  hdr?: boolean;
}

export const INPUT_SPACES: readonly ColorSpaceInfo<InputSpaceId>[] = [
  { id: 'rec709', label: 'Rec.709 (Gamma 2.4)' },
  { id: 'srgb', label: 'sRGB' },
  { id: 'rec2020', label: 'Rec.2020 (Gamma 2.4)' },
  { id: 'hlg', label: 'Rec.2100 HLG', hdr: true },
  { id: 'pq', label: 'Rec.2100 PQ (ST 2084)', hdr: true },
  { id: 'slog3', label: 'Sony S-Log3 / S-Gamut3.Cine', hdr: true },
  { id: 'vlog', label: 'Panasonic V-Log / V-Gamut', hdr: true },
  { id: 'logc3', label: 'ARRI LogC3 (EI 800) / AWG3', hdr: true },
  { id: 'logc4', label: 'ARRI LogC4 / AWG4', hdr: true },
  { id: 'clog3', label: 'Canon Log 3 / Cinema Gamut', hdr: true },
  { id: 'bmdfilm5', label: 'Blackmagic Film Gen 5 / BMD Wide Gamut', hdr: true },
];

export const TONE_MAPS: readonly { id: ToneMapId; label: string }[] = [
  { id: 'none', label: 'None (clip)' },
  { id: 'soft', label: 'Soft highlight roll-off' },
];

export const OUTPUT_SPACES: readonly ColorSpaceInfo<OutputSpaceId>[] = [
  { id: 'rec709', label: 'Rec.709 (Gamma 2.4)' },
  { id: 'rec2020', label: 'Rec.2020 (Gamma 2.4)' },
  { id: 'hlg', label: 'Rec.2100 HLG', hdr: true },
  { id: 'pq', label: 'Rec.2100 PQ (ST 2084)', hdr: true },
];

/** A clip's input colour space (ext.colorSpace). Absent = Rec.709, nothing to do. */
export interface ClipColorSpace {
  input: InputSpaceId;
  toneMap?: ToneMapId;
}

export function sanitizeColorSpace(raw: unknown): ClipColorSpace | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const r = raw as Record<string, unknown>;
  const input = INPUT_SPACES.find((s) => s.id === r.input)?.id;
  if (!input || input === 'rec709') return undefined;
  const out: ClipColorSpace = { input };
  if (r.toneMap === 'soft') out.toneMap = 'soft';
  return out;
}

export const isOutputSpace = (v: unknown): v is OutputSpaceId => OUTPUT_SPACES.some((s) => s.id === v);
/** The project's output space ('' and unknown values = Rec.709). */
export const outputSpaceOf = (v: string | undefined | null): OutputSpaceId => (isOutputSpace(v) ? v : 'rec709');

// ---------------------------------------------------------------------------
// Transfer functions (encoded value → scene linear, 0.18 = mid grey)
// ---------------------------------------------------------------------------

/** Legal-range decoded video (0..1) → full-scale code value / 1023. */
export const toCodeValue = (v: number) => (v * 876 + 64) / 1023;
/** Full-scale code value / 1023 → decoded video level (0..1). */
export const fromCodeValue = (cv: number) => (cv * 1023 - 64) / 876;

/** Sony S-Log3 (code value / 1023 → reflectance). */
export function slog3ToLinear(x: number): number {
  return x >= 171.2102946929 / 1023 ? Math.pow(10, (x * 1023 - 420) / 261.5) * (0.18 + 0.01) - 0.01 : ((x * 1023 - 95) * 0.01125) / (171.2102946929 - 95);
}
export function linearToSlog3(y: number): number {
  return y >= 0.01125 ? (420 + Math.log10((y + 0.01) / (0.18 + 0.01)) * 261.5) / 1023 : (y * (171.2102946929 - 95)) / 0.01125 / 1023 + 95 / 1023;
}

/** Panasonic V-Log. */
export function vlogToLinear(x: number): number {
  return x < 0.181 ? (x - 0.125) / 5.6 : Math.pow(10, (x - 0.598206) / 0.241514) - 0.00873;
}
export function linearToVlog(y: number): number {
  return y < 0.01 ? 5.6 * y + 0.125 : 0.241514 * Math.log10(y + 0.00873) + 0.598206;
}

/** ARRI LogC3 (EI 800, scene linear). */
const C3 = { cut: 0.010591, a: 5.555556, b: 0.052272, c: 0.24719, d: 0.385537, e: 5.367655, f: 0.092809 };
export function logc3ToLinear(x: number): number {
  return x > C3.e * C3.cut + C3.f ? (Math.pow(10, (x - C3.d) / C3.c) - C3.b) / C3.a : (x - C3.f) / C3.e;
}
export function linearToLogc3(y: number): number {
  return y > C3.cut ? C3.c * Math.log10(C3.a * y + C3.b) + C3.d : C3.e * y + C3.f;
}

/** ARRI LogC4. */
const C4a = (Math.pow(2, 18) - 16) / 117.45;
const C4b = (1023 - 95) / 1023;
const C4c = 95 / 1023;
const C4s = (7 * Math.LN2 * Math.pow(2, 7 - (14 * C4c) / C4b)) / (C4a * C4b);
const C4t = (Math.pow(2, 14 * (-C4c / C4b) + 6) - 64) / C4a;
export function logc4ToLinear(x: number): number {
  return x >= 0 ? (Math.pow(2, (14 * (x - C4c)) / C4b + 6) - 64) / C4a : x * C4s + C4t;
}
export function linearToLogc4(y: number): number {
  return y >= C4t ? ((Math.log2(C4a * y + 64) - 6) / 14) * C4b + C4c : (y - C4t) / C4s;
}

/** Canon Log 3 (legal-range signal → linear; the curve's 1.0 is 90% reflectance, hence × 0.9). */
export function clog3ToLinear(x: number): number {
  let y: number;
  if (x < 0.097465473) y = -(Math.pow(10, (0.12783901 - x) / 0.36726845) - 1) / 14.98325;
  else if (x <= 0.15277891) y = (x - 0.12512219) / 1.9754798;
  else y = (Math.pow(10, (x - 0.12240537) / 0.36726845) - 1) / 14.98325;
  return y * 0.9;
}
export function linearToClog3(r: number): number {
  const y = r / 0.9;
  if (y < -0.014) return -0.36726845 * Math.log10(1 - 14.98325 * y) + 0.12783901;
  if (y <= 0.014) return 1.9754798 * y + 0.12512219;
  return 0.36726845 * Math.log10(14.98325 * y + 1) + 0.12240537;
}

/** Blackmagic Film Generation 5. */
const BM = { A: 0.08692876065491224, B: 0.005494072432257808, C: 0.5300133392291939, D: 8.283605932402494, E: 0.09246575342465753, cut: 0.005 };
export function bmdFilm5ToLinear(x: number): number {
  return x < BM.D * BM.cut + BM.E ? (x - BM.E) / BM.D : Math.exp((x - BM.C) / BM.A) - BM.B;
}
export function linearToBmdFilm5(y: number): number {
  return y < BM.cut ? BM.D * y + BM.E : BM.A * Math.log(y + BM.B) + BM.C;
}

/** sRGB EOTF (IEC 61966-2-1). */
export const srgbToLinear = (v: number) => (v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4));

/** HLG (BT.2100) inverse OETF: signal → scene light 0..1. */
const HLG = { a: 0.17883277, b: 1 - 4 * 0.17883277, c: 0.5 - 0.17883277 * Math.log(4 * 0.17883277) };
export function hlgToScene(e: number): number {
  return e <= 0.5 ? (e * e) / 3 : (Math.exp((e - HLG.c) / HLG.a) + HLG.b) / 12;
}
export function sceneToHlg(l: number): number {
  return l <= 1 / 12 ? Math.sqrt(3 * Math.max(0, l)) : HLG.a * Math.log(12 * l - HLG.b) + HLG.c;
}
/** Scene light of HLG reference white (75% signal, BT.2408). */
export const HLG_REFERENCE = hlgToScene(0.75);

/** PQ (ST 2084): signal ↔ display light in nits. */
const PQ = { m1: 2610 / 16384, m2: (2523 / 4096) * 128, c1: 3424 / 4096, c2: (2413 / 4096) * 32, c3: (2392 / 4096) * 32 };
export function pqToNits(e: number): number {
  const p = Math.pow(Math.max(0, e), 1 / PQ.m2);
  return 10000 * Math.pow(Math.max(0, p - PQ.c1) / (PQ.c2 - PQ.c3 * p), 1 / PQ.m1);
}
export function nitsToPq(n: number): number {
  const y = Math.pow(Math.max(0, n) / 10000, PQ.m1);
  return Math.pow((PQ.c1 + PQ.c2 * y) / (1 + PQ.c3 * y), PQ.m2);
}
/** HDR reference white (BT.2408): the working space's 1.0. */
export const HDR_REFERENCE_NITS = 203;

// ---------------------------------------------------------------------------
// Gamuts
// ---------------------------------------------------------------------------

type Mat3 = number[];
type Primaries = [[number, number], [number, number], [number, number], [number, number]];

/** RGB → XYZ for primaries (r, g, b) and white, as xy chromaticities. */
function rgbToXyz([r, g, b, w]: Primaries): Mat3 {
  const col = ([x, y]: [number, number]) => [x / y, 1, (1 - x - y) / y];
  const R = col(r);
  const G = col(g);
  const B = col(b);
  const W = col(w);
  const m = [R[0], G[0], B[0], R[1], G[1], B[1], R[2], G[2], B[2]];
  const s = mul3v(inv3(m), W);
  return [m[0] * s[0], m[1] * s[1], m[2] * s[2], m[3] * s[0], m[4] * s[1], m[5] * s[2], m[6] * s[0], m[7] * s[1], m[8] * s[2]];
}

function inv3(m: Mat3): Mat3 {
  const [a, b, c, d, e, f, g, h, i] = m;
  const A = e * i - f * h;
  const B = -(d * i - f * g);
  const C = d * h - e * g;
  const det = a * A + b * B + c * C;
  return [A / det, -(b * i - c * h) / det, (b * f - c * e) / det, B / det, (a * i - c * g) / det, -(a * f - c * d) / det, C / det, -(a * h - b * g) / det, (a * e - b * d) / det];
}
function mul3(a: Mat3, b: Mat3): Mat3 {
  const o: number[] = [];
  for (let r = 0; r < 3; r++) for (let c = 0; c < 3; c++) o.push(a[r * 3] * b[c] + a[r * 3 + 1] * b[3 + c] + a[r * 3 + 2] * b[6 + c]);
  return o;
}
const mul3v = (m: Mat3, v: number[]) => [m[0] * v[0] + m[1] * v[1] + m[2] * v[2], m[3] * v[0] + m[4] * v[1] + m[5] * v[2], m[6] * v[0] + m[7] * v[1] + m[8] * v[2]];

const D65: [number, number] = [0.3127, 0.329];
export const GAMUTS = {
  rec709: [[0.64, 0.33], [0.3, 0.6], [0.15, 0.06], D65],
  rec2020: [[0.708, 0.292], [0.17, 0.797], [0.131, 0.046], D65],
  sgamut3cine: [[0.766, 0.275], [0.225, 0.8], [0.089, -0.087], D65],
  vgamut: [[0.73, 0.28], [0.165, 0.84], [0.1, -0.03], D65],
  awg3: [[0.684, 0.313], [0.221, 0.848], [0.0861, -0.102], D65],
  awg4: [[0.7347, 0.2653], [0.1424, 0.8576], [0.0991, -0.0308], D65],
  cinemaGamut: [[0.74, 0.27], [0.17, 1.14], [0.08, -0.1], D65],
  bmdWide: [[0.7177215, 0.3171181], [0.228041, 0.861569], [0.1005841, -0.0820452], [0.312717, 0.3290312]],
} satisfies Record<string, Primaries>;

/** Linear RGB in gamut `from` → linear RGB in gamut `to` (no chromatic adaptation: the whites are D65). */
export function gamutMatrix(from: Primaries, to: Primaries): Mat3 {
  return mul3(inv3(rgbToXyz(to)), rgbToXyz(from));
}

// ---------------------------------------------------------------------------
// Tone mapping and the transforms
// ---------------------------------------------------------------------------

/** Where the soft roll-off starts (linear, working white = 1). */
export const TONE_KNEE = 0.8;

/**
 * Soft highlight roll-off on scene light: values below the knee are kept,
 * above it the largest channel is compressed smoothly towards 1 (never
 * reaching it) and the others scaled with it, so hues stay put.
 */
export function toneMapSoft(rgb: number[]): void {
  const m = Math.max(rgb[0], rgb[1], rgb[2]);
  if (m <= TONE_KNEE) return;
  const k = TONE_KNEE;
  const x = (m - k) / (1 - k);
  const mapped = k + ((1 - k) * x) / (1 + x);
  const s = mapped / m;
  for (let i = 0; i < 3; i++) rgb[i] *= s;
}

const GAMMA = 2.4;
const encode24 = (l: number) => Math.pow(Math.min(1, Math.max(0, l)), 1 / GAMMA);

interface InputDef {
  /** Signal → linear (camera gamut). */
  decode: (v: number) => number;
  gamut: Primaries;
}

const INPUT_DEFS: Record<Exclude<InputSpaceId, 'rec709'>, InputDef> = {
  srgb: { decode: srgbToLinear, gamut: GAMUTS.rec709 },
  rec2020: { decode: (v) => Math.pow(Math.max(0, v), GAMMA), gamut: GAMUTS.rec2020 },
  hlg: { decode: (v) => hlgToScene(Math.max(0, v)) / HLG_REFERENCE, gamut: GAMUTS.rec2020 },
  pq: { decode: (v) => pqToNits(v) / HDR_REFERENCE_NITS, gamut: GAMUTS.rec2020 },
  slog3: { decode: (v) => slog3ToLinear(toCodeValue(v)), gamut: GAMUTS.sgamut3cine },
  vlog: { decode: (v) => vlogToLinear(toCodeValue(v)), gamut: GAMUTS.vgamut },
  logc3: { decode: (v) => logc3ToLinear(toCodeValue(v)), gamut: GAMUTS.awg3 },
  logc4: { decode: (v) => logc4ToLinear(toCodeValue(v)), gamut: GAMUTS.awg4 },
  clog3: { decode: clog3ToLinear, gamut: GAMUTS.cinemaGamut },
  bmdfilm5: { decode: bmdFilm5ToLinear, gamut: GAMUTS.bmdWide },
};

export type ColorFn = (rgb: number[]) => void;

/** The input transform as an in-place pixel function (0..1 in and out), or null for Rec.709. */
export function inputTransform(cs: ClipColorSpace | undefined | null): ColorFn | null {
  if (!cs || cs.input === 'rec709') return null;
  const def = INPUT_DEFS[cs.input];
  if (!def) return null;
  const m = gamutMatrix(def.gamut, GAMUTS.rec709);
  const soft = cs.toneMap === 'soft';
  return (rgb) => {
    const lin = [def.decode(rgb[0]), def.decode(rgb[1]), def.decode(rgb[2])];
    const o = mul3v(m, lin);
    for (let i = 0; i < 3; i++) o[i] = Math.max(0, o[i]);
    if (soft) toneMapSoft(o);
    for (let i = 0; i < 3; i++) rgb[i] = encode24(o[i]);
  };
}

/** The output transform of an export (working space → output space), or null for Rec.709. */
export function outputTransform(space: OutputSpaceId): ColorFn | null {
  if (space === 'rec709') return null;
  const m = gamutMatrix(GAMUTS.rec709, GAMUTS.rec2020);
  return (rgb) => {
    const lin = rgb.map((v) => Math.pow(Math.min(1, Math.max(0, v)), GAMMA));
    const o = mul3v(m, lin).map((v) => Math.max(0, v));
    for (let i = 0; i < 3; i++) {
      if (space === 'rec2020') rgb[i] = encode24(o[i]);
      else if (space === 'pq') rgb[i] = nitsToPq(o[i] * HDR_REFERENCE_NITS);
      else rgb[i] = sceneToHlg(o[i] * HLG_REFERENCE);
    }
  };
}

// ---------------------------------------------------------------------------
// ACEScct working space
// ---------------------------------------------------------------------------

/**
 * The project's grading space. 'rec709' (the default) grades the display
 * signal (Rec.709 primaries, gamma 2.4). 'acescct' grades clips that have
 * correction nodes in ACEScct: the input transform decodes the footage to
 * scene light, converts it to AP1 primaries (Bradford adaptation from D65
 * to the ACES white) and encodes ACEScct (Academy S-2016-001); after the
 * nodes the inverse brings it back to Rec.709 gamma 2.4 (with the clip's
 * soft roll-off, if any), where the rest of the pipeline continues. The
 * curve and primaries are the published ACES ones, but the way out is a
 * plain inverse rather than the ACES Output Transform (RRT + ODT), so it is
 * labelled "ACEScct working space", not ACES.
 */
export type WorkingSpaceId = 'rec709' | 'acescct';
export const WORKING_SPACES: readonly ColorSpaceInfo<WorkingSpaceId>[] = [
  { id: 'rec709', label: 'Rec.709 (Gamma 2.4)' },
  { id: 'acescct', label: 'ACEScct working space (AP1)' },
];
export const isWorkingSpace = (v: unknown): v is WorkingSpaceId => WORKING_SPACES.some((s) => s.id === v);
/** The project's working space ('' and unknown values = Rec.709). */
export const workingSpaceOf = (v: string | undefined | null): WorkingSpaceId => (isWorkingSpace(v) ? v : 'rec709');

/** ACES AP1 primaries (ACEScg / ACEScct) and the ACES white point. */
export const ACES_WHITE: [number, number] = [0.32168, 0.33767];
export const AP1: Primaries = [[0.713, 0.293], [0.165, 0.83], [0.128, 0.044], ACES_WHITE];

/** ACEScct (S-2016-001): linear AP1 → code value. */
export function linearToAcescct(v: number): number {
  return v <= 0.0078125 ? 10.5402377416545 * v + 0.0729055341958355 : (Math.log2(v) + 9.72) / 17.52;
}
export function acescctToLinear(v: number): number {
  if (v <= 0.155251141552511) return (v - 0.0729055341958355) / 10.5402377416545;
  return v < (Math.log2(65504) + 9.72) / 17.52 ? Math.pow(2, v * 17.52 - 9.72) : 65504;
}

const BRADFORD = [0.8951, 0.2664, -0.1614, -0.7502, 1.7135, 0.0367, 0.0389, -0.0685, 1.0296];
/** Bradford chromatic adaptation between two white points (xy), on XYZ. */
function bradford(from: [number, number], to: [number, number]): Mat3 {
  const xyz = ([x, y]: [number, number]) => [x / y, 1, (1 - x - y) / y];
  const s = mul3v(BRADFORD, xyz(from));
  const d = mul3v(BRADFORD, xyz(to));
  const D = [d[0] / s[0], 0, 0, 0, d[1] / s[1], 0, 0, 0, d[2] / s[2]];
  return mul3(inv3(BRADFORD), mul3(D, BRADFORD));
}

/** Linear RGB in gamut `from` → gamut `to`, with Bradford adaptation when their whites differ. */
export function gamutMatrixAdapted(from: Primaries, to: Primaries): Mat3 {
  const same = Math.abs(from[3][0] - to[3][0]) < 1e-6 && Math.abs(from[3][1] - to[3][1]) < 1e-6;
  if (same) return gamutMatrix(from, to);
  return mul3(inv3(rgbToXyz(to)), mul3(bradford(from[3], to[3]), rgbToXyz(from)));
}

const DECODE_709 = (v: number) => Math.pow(Math.max(0, v), GAMMA);

/** A clip's input transform into the ACEScct working space (its colour space, Rec.709 when it has none). */
export function workingInputTransform(cs: ClipColorSpace | undefined | null): ColorFn {
  const def: InputDef = cs && cs.input !== 'rec709' ? INPUT_DEFS[cs.input] : { decode: DECODE_709, gamut: GAMUTS.rec709 };
  const m = gamutMatrixAdapted(def.gamut, AP1);
  return (rgb) => {
    const lin = [def.decode(rgb[0]), def.decode(rgb[1]), def.decode(rgb[2])];
    const o = mul3v(m, lin);
    for (let i = 0; i < 3; i++) rgb[i] = Math.min(1, Math.max(0, linearToAcescct(Math.max(0, o[i]))));
  };
}

/** ACEScct → the Rec.709 display signal, after a clip's nodes (with its soft roll-off, if it has one). */
export function workingOutputTransform(cs: ClipColorSpace | undefined | null): ColorFn {
  const m = gamutMatrixAdapted(AP1, GAMUTS.rec709);
  const soft = cs?.toneMap === 'soft';
  return (rgb) => {
    const o = mul3v(m, [acescctToLinear(rgb[0]), acescctToLinear(rgb[1]), acescctToLinear(rgb[2])]);
    for (let i = 0; i < 3; i++) o[i] = Math.max(0, o[i]);
    if (soft) toneMapSoft(o);
    for (let i = 0; i < 3; i++) rgb[i] = encode24(o[i]);
  };
}

/** Cache keys of a clip's working-space transforms. */
export const workingInputKey = (cs: ClipColorSpace | undefined | null) => `acescct-in:${colorSpaceKey(cs) || 'rec709'}`;
export const workingOutputKey = (cs: ClipColorSpace | undefined | null) => `acescct-out:${cs?.toneMap === 'soft' ? 'soft' : 'none'}`;

/** Input / output transforms are smooth curves with gamut matrices: a finer LUT than the grade's. */
export const COLOR_SPACE_LUT_SIZE = 65;

export function bakeColorFn(f: ColorFn, size = COLOR_SPACE_LUT_SIZE): CubeLut {
  const data = new Float32Array(size * size * size * 3);
  const px = [0, 0, 0];
  let i = 0;
  for (let b = 0; b < size; b++) {
    for (let g = 0; g < size; g++) {
      for (let r = 0; r < size; r++, i += 3) {
        px[0] = r / (size - 1);
        px[1] = g / (size - 1);
        px[2] = b / (size - 1);
        f(px);
        data[i] = px[0];
        data[i + 1] = px[1];
        data[i + 2] = px[2];
      }
    }
  }
  return { size, data, domainMin: [0, 0, 0], domainMax: [1, 1, 1] };
}

/** Key of a clip's input transform (for caching baked LUTs); '' = none. */
export const colorSpaceKey = (cs: ClipColorSpace | undefined | null) => (cs && cs.input !== 'rec709' ? `${cs.input}:${cs.toneMap ?? 'none'}` : '');

/** Encoder colour tags for an output space (yuv outputs). */
export function outputColorTags(space: OutputSpaceId, tenBit: boolean): { colorspace: string; primaries: string; trc: string } {
  switch (space) {
    case 'rec2020':
      return { colorspace: 'bt2020nc', primaries: 'bt2020', trc: tenBit ? 'bt2020-10' : 'bt709' };
    case 'hlg':
      return { colorspace: 'bt2020nc', primaries: 'bt2020', trc: 'arib-std-b67' };
    case 'pq':
      return { colorspace: 'bt2020nc', primaries: 'bt2020', trc: 'smpte2084' };
    default:
      return { colorspace: 'bt709', primaries: 'bt709', trc: 'bt709' };
  }
}
